import { useAuthStore } from '../store/auth.store';

/** A file the user attached: extracted text for the model, bytes in cloud storage. */
export interface Attachment {
  fileId: string;
  name: string;
  mimeType: string;
  size: number;
  content: string;
}

export interface ConversationFile {
  fileId: string;
  name: string;
  mimeType: string;
  url: string;
  createdAt: string;
}

/**
 * Signed GET URLs expire, so they are fetched on demand rather than held in
 * state — a link stored at render time would be dead minutes later.
 */
export async function fetchConversationFiles(
  conversationId: string,
): Promise<ConversationFile[]> {
  const res = await apiFetch(
    `/api/chat/attachments/conversation/${conversationId}`,
  );
  if (!res.ok) throw new Error('Could not load files');
  const { files } = (await res.json()) as { files: ConversationFile[] };
  return files;
}

/**
 * Presign → PUT direct to storage → confirm. The bytes never touch our API;
 * it only mints a short-lived, tightly-scoped permission.
 */
export async function uploadFile(
  conversationId: string,
  file: File,
): Promise<string> {
  const mimeType = file.type || 'application/octet-stream';

  const presign = await apiFetch('/api/chat/attachments/presign', {
    method: 'POST',
    body: JSON.stringify({
      conversationId,
      file: { name: file.name, mimeType, size: file.size },
    }),
  });
  if (!presign.ok) throw new Error('Could not start upload');
  const { fileId, uploadUrl, uploadMethod } = await presign.json();

  // Bare fetch: our auth header would break the signature, and Content-Type
  // must match the value that was signed.
  const put = await fetch(uploadUrl, {
    method: uploadMethod,
    headers: { 'Content-Type': mimeType },
    body: file,
  });
  if (!put.ok) throw new Error('Upload failed');

  const done = await apiFetch(`/api/chat/attachments/${fileId}/complete`, {
    method: 'POST',
  });
  if (!done.ok) throw new Error('Could not confirm upload');

  return fileId;
}

export async function apiFetch(path: string, init?: RequestInit) {
  const token = useAuthStore.getState().accessToken;
  return fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
}
